import { json } from "@codemirror/lang-json";
import { python } from "@codemirror/lang-python";
import CancelOutlined from "@mui/icons-material/CancelOutlined";
import PlayArrow from "@mui/icons-material/PlayArrow";
import Save from "@mui/icons-material/Save";
import {
  IconButton,
  SxProps,
  Typography,
  useTheme,
} from "@mui/material";
import { CasLoader } from "./brand/CasLoader";
import { CSSProperties } from "@mui/styles";
import { Box } from "@mui/system";
import ReactCodeMirror, {
  Extension,
  Prec,
  keymap,
} from "@uiw/react-codemirror";
import React, { createContext, useEffect, useRef, useState } from "react";
import { useCodeEditor, useCodeEditorOqlExtensions, useCodeEditorPythonExtensions } from "../hook/editor";
import { useIsDarkMode } from "../hook/setting";
import { EditorContextValue } from "../types/editor";

export const CodeEditorToolbar: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
      }}
    >
      {children}
    </Box>
  );
};
export const CodeEditorHeader: React.FC<{
  children?: React.ReactNode;
}> = ({ children }) => {
  const { title } = useCodeEditor();
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        height: "3rem",
      }}
    >
      <Typography>{title}</Typography>
      <Box>{children}</Box>
    </Box>
  );
};

export const CodeEditorCode: React.FC<{
  extensions: Extension[];
}> = ({ extensions }) => {
  const { setCode, setUnsavedChanges, code } = useCodeEditor();
  const { isDarkMode } = useIsDarkMode();

  return (
    <ReactCodeMirror
      editable={true}
      value={code}
      theme={isDarkMode ? "dark" : "light"}
      height="40rem"
      extensions={extensions}
      onChange={(value: any) => {
        setUnsavedChanges(true);
        setCode(value);
      }}
    />
  );
};

export const CodeEditorCodePython: React.FC = () => {
  const { extensions } = useCodeEditorPythonExtensions();

  return <CodeEditorCode extensions={extensions} />;
};

export const CodeEditorCodeOql: React.FC = () => {
  const { extensions } = useCodeEditorOqlExtensions();

  return <CodeEditorCode extensions={extensions} />;
};

export const CodeEditorCodeJson: React.FC = ({}) => {
  const { onSave } = useCodeEditor();
  const codeKeyMap = Prec.highest(
    keymap.of([
      {
        key: "Ctrl-s",
        preventDefault: true,
        run: () => {
          onSave();
          return true;
        },
      },
    ]),
  );
  const extensions = [codeKeyMap, json()];
  return <CodeEditor extensions={extensions} />;
};

export const CodeEditor: React.FC<{
  extensions: Extension[];
}> = ({ extensions }) => {
  const {
    onSave,
    onCancel,
    unsavedChanges,
    isSaving,
    setCode,
    setIsSaving,
    setUnsavedChanges,
    code,
  } = useCodeEditor();
  const editControlsDisabled = !unsavedChanges || isSaving;
  const hideCircularProgessSxProps: SxProps = isSaving
    ? {}
    : { display: "none" };
  const { isDarkMode } = useIsDarkMode();

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          height: "3rem",
        }}
      >
        <Typography variant="subtitle1">Code</Typography>
        <Box
          sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}
        >
          <CasLoader
            sx={hideCircularProgessSxProps}
            size={24}
          />
          <IconButton
            color="primary"
            onClick={onSave}
            disabled={editControlsDisabled}
          >
            <Save />
          </IconButton>
          <IconButton
            color="primary"
            onClick={onCancel}
            disabled={editControlsDisabled}
          >
            <CancelOutlined />
          </IconButton>
        </Box>
      </Box>
      <ReactCodeMirror
        editable={true}
        value={code}
        theme={isDarkMode ? "dark" : "light"}
        height="40rem"
        extensions={extensions}
        onChange={(value: any) => {
          setUnsavedChanges(true);
          setCode(value);
        }}
      />
    </>
  );
};

export const CodeEditorCodeLogs: React.FC = () => {
  const { logsLoading, logs } = useCodeEditor();
  const theme = useTheme();
  const { isDarkMode } = useIsDarkMode();
  const colorProps: CSSProperties = isDarkMode
    ? { backgroundColor: "rgb(29, 31, 33)", color: "rgb(197, 200, 198)" }
    : { backgroundColor: theme.palette.grey.A200 };
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [logs]);

  return (
    <>
      {logsLoading ? (
        <Box
          sx={{ padding: "1rem", display: "flex", justifyContent: "center" }}
        >
          <CasLoader />
        </Box>
      ) : (
        <div
          ref={scrollRef}
          style={{
            width: "100%",
            height: "40rem",
            padding: "1rem",
            overflowY: "auto",
            whiteSpace: "pre",
            ...colorProps,
          }}
        >
          {logs ? logs : "No Logs"}
        </div>
      )}
    </>
  );
};

export const CodeEditorToolbarCancelButton: React.FC = () => {
  const { onCancel, editControlsDisabled } = useCodeEditor();
  return (
    <IconButton
      color="primary"
      onClick={onCancel}
      disabled={editControlsDisabled}
    >
      <CancelOutlined />
    </IconButton>
  );
};

export const CodeEditorToolbarSaveButton: React.FC = () => {
  const { onSave, editControlsDisabled } = useCodeEditor();
  return (
    <IconButton
      color="primary"
      onClick={onSave}
      disabled={editControlsDisabled}
    >
      <Save />
    </IconButton>
  );
};

export const CodeEditorToolbarCircularProgress: React.FC = () => {
  const { isSaving } = useCodeEditor();
  const hideCircularProgessSxProps: SxProps = isSaving
    ? {}
    : { display: "none" };
  return (
    <CasLoader sx={hideCircularProgessSxProps} size={24} />
  );
};

export const CodeEditorToolbarRunButton: React.FC = () => {
  const { onRun, isSaving } = useCodeEditor();
  return (
    <IconButton color="primary" onClick={onRun} disabled={isSaving}>
      <PlayArrow />
    </IconButton>
  );
};

export const CodeEditorContext = createContext<EditorContextValue | undefined>(
  undefined,
);

export const CodeEditorProvider: React.FC<{
  codeState?: string;
  onSaveCallback?: (code: string) => Promise<any>;
  onRunCallback?: () => Promise<any>;
  logs?: string;
  children?: React.ReactNode;
  title?: string;
}> = ({
  codeState,
  onSaveCallback = (code: string) => Promise.resolve(undefined),
  onRunCallback = () => Promise.resolve(undefined),
  logs,
  children,
  title = "Code",
}) => {
  const [code, _setCode] = useState("");
  const [logsLoading, setLogsLoading] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const editControlsDisabled = !unsavedChanges || isSaving;

  const setCode = (code: string) => {
    const cleanedCode = code.replace(/^```json\s*|\s*```$/g, "");
    _setCode(cleanedCode);
  };

  useEffect(() => {
    setCode(codeState ?? "");
  }, [codeState]);

  const onCancel = () => {
    setCode(codeState ?? "");
    setUnsavedChanges(false);
  };

  const onSave = async () => {
    if (isSaving) return;

    setIsSaving(true);
    return onSaveCallback(code)
      .then(() => {
        setIsSaving(false);
        setUnsavedChanges(false);
      })
      .finally(() => {
        setIsSaving(false);
        setUnsavedChanges(false);
      });
  };

  const onRun = async () => {
    try {
      setIsSaving(true);
      if (unsavedChanges) {
        await onSaveCallback(code);
      }
      await onRunCallback();
    } catch (e) {}
    setIsSaving(false);
    setUnsavedChanges(false);
  };

  const ctxValue: EditorContextValue = {
    onSave,
    onCancel,
    onRun,
    code,
    setCode,
    unsavedChanges,
    setUnsavedChanges,
    isSaving,
    setIsSaving,
    editControlsDisabled,
    logsLoading,
    setLogsLoading,
    logs,
    title,
  };

  return (
    <CodeEditorContext.Provider value={ctxValue}>
      {children}
    </CodeEditorContext.Provider>
  );
};
